import styled from "@emotion/styled";
import {
  SHARED_SEND_MESSAGE_CACHE_KEY,
  useSendMessageMutation,
} from "@financial-advisory/data-access/financial-advisory-api-service";
import { PromptInput } from "@financial-advisory/ui/components/inputs";
import { useChatMessageState } from "../hooks";
import { MessageAuthor } from "../types";

type InputProps = {
  placeholder?: string;
  className?: string;
};

const Input = ({ placeholder, className }: InputProps = {}) => {
  const messagesState = useChatMessageState();
  const { pushMessage, updateMessage, messages, sessionId, setSessionId } =
    messagesState;
  const [sendMessageMutation] = useSendMessageMutation({
    fixedCacheKey: SHARED_SEND_MESSAGE_CACHE_KEY,
  });

  const lastBotMessage = [...messages]
    .reverse()
    .find((msg) => msg.author === MessageAuthor.Bot);

  const userCanSendMessage = lastBotMessage?.text !== "" || !lastBotMessage;

  const handleSendUserMessage = async (text: string) => {
    const normalisedText = text.trim();
    if (!normalisedText) return;
    pushMessage({
      author: MessageAuthor.User,
      text: normalisedText,
    });

    const latestBotMessageId = pushMessage({
      author: MessageAuthor.Bot,
      text: "",
    });

    try {
      const result = await sendMessageMutation({
        message: normalisedText,
        session_id: sessionId,
      });

      if ("data" in result && result.data) {
        const { message, session_id } = result.data;
        updateMessage({
          id: latestBotMessageId,
          text: message,
        });
        setSessionId(session_id);
      }
    } catch (error) {
      console.error("Failed to send message to backend", error);
      updateMessage({
        id: latestBotMessageId,
        text: "I'm sorry, I couldn't process your request. Please try again later.",
      });
    }
  };

  return (
    <PromptInput
      className={className}
      placeholder={placeholder ?? "Type your message here..."}
      onSubmit={handleSendUserMessage}
      maxRows={7}
      isDisabled={!userCanSendMessage}
    />
  );
};

export const MessageInput = styled(Input)`
  background-color: ${({ theme }) => theme.colors.component.background[1]};
  border-radius: ${({ theme }) => theme.roundness.md};
  border: 0.0625rem solid ${({ theme }) => theme.colors.component.border[1]};
`;
